import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { api } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { UserAvatar } from '@/components/UserAvatar';
import ProjectSearchPicker from '@/components/ProjectSearchPicker';
import BreadcrumbNav from '@/components/BreadcrumbNav';
import { 
  ArrowLeft, Calendar, Clock, MapPin, Building2, Edit, Plus,
  RefreshCw, CheckCircle, Phone, Users, Gift, CalendarClock, X,
  MoreVertical, DollarSign, ChevronDown, ChevronUp, AlertCircle, AlertTriangle,
  FileText, Eye, Briefcase, Home, Activity, FolderOpen, StickyNote, Send,
  MessageCircle, Copy, ExternalLink, Loader2, Sparkles, RotateCcw, Upload, FileSpreadsheet
} from 'lucide-react';
import { toast } from 'sonner';
import { MobileMenuTrigger } from '@/components/Layout';

// Stage configuration for Rep view - HWC (Hot/Warm/Cold) stages
const STAGE_OPTIONS = [
  { value: 'Hot', label: '🔥 Hot', color: 'bg-red-100 text-red-700' },
  { value: 'Warm', label: '☀️ Warm', color: 'bg-amber-100 text-amber-700' },
  { value: 'Cold', label: '❄️ Cold', color: 'bg-blue-100 text-blue-700' },
];

// Lost reasons dropdown - must match backend valid_reasons
const LOST_REASONS = [
  'Bought Elsewhere',
  'Not Reachable/No Response',
  'Not Interested/Dropped Plan',
  'Budget Mismatch',
  'Product/Location Mismatch',
  'Delayed(On Hold)',
  'Other'
];

// Cancel reasons for visits
const VISIT_CANCEL_REASONS = [
  'Customer cancelled',
  'Customer not reachable',
  'Site issue',
  'Rep unavailable',
  'Other'
];

const CustomerDetailRep = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { customerId } = useParams();
  const [customer, setCustomer] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeActivityTab, setActiveActivityTab] = useState('all');
  
  // Back navigation handler - returns to previous screen or falls back to CRM
  const handleBack = () => {
    // Check if there's navigation history (came from another page in the app)
    // window.history.length > 2 means there's at least one previous page to go back to
    // (1 = initial, 2 = current page)
    if (window.history.length > 2) {
      navigate(-1); // Go back preserving context (filters, scroll, etc.)
    } else {
      navigate('/crm'); // Fallback to CRM list if no history
    }
  };
  
  // Dialog states
  const [showScheduleVisitDialog, setShowScheduleVisitDialog] = useState(false);
  const [showFollowupDialog, setShowFollowupDialog] = useState(false);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [showLostDialog, setShowLostDialog] = useState(false);
  const [showEditCustomerDialog, setShowEditCustomerDialog] = useState(false);
  const [showRequirementDialog, setShowRequirementDialog] = useState(false);
  const [showReferralDialog, setShowReferralDialog] = useState(false);
  
  // Visit lifecycle dialogs
  const [showCompleteVisitDialog, setShowCompleteVisitDialog] = useState(false);
  const [showCancelVisitDialog, setShowCancelVisitDialog] = useState(false);
  const [showRescheduleVisitDialog, setShowRescheduleVisitDialog] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [showFollowupsExpanded, setShowFollowupsExpanded] = useState(false);
  
  // Form states
  const [scheduleVisitForm, setScheduleVisitForm] = useState({
    project_id: '', project_name: '', visit_date: '', visit_time: '10:00', notes: '', auto_reminder: true, meeting_type: 'Site Visit', obm_purpose: ''
  });
  const [completeVisitForm, setCompleteVisitForm] = useState({
    project_id: '', project_name: '', completed_date: '', completed_time: '', remarks: '', next_followup_date: '', next_followup_time: '', lead_warmth: ''
  });
  const [cancelVisitForm, setCancelVisitForm] = useState({ cancel_reason: '', notes: '' });
  const [rescheduleForm, setRescheduleForm] = useState({ new_date: '', new_time: '10:00', notes: '' });
  const [followupForm, setFollowupForm] = useState({ due_date: '', due_time: '10:00', notes: '', type: 'Call follow-up' });
  const [lostForm, setLostForm] = useState({ reason: '', notes: '' });
  const [requirementForm, setRequirementForm] = useState({ budgetMin: '', budgetMax: '', location: '', purpose: '' });
  const [bookingForm, setBookingForm] = useState({ project_id: '', project_name: '', agreement_value: '', booking_date: '', remarks: '' });
  const [referralForm, setReferralForm] = useState({ name: '', phone: '', email: '' });
  const [editCustomerForm, setEditCustomerForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    budget_min_lacs: '',
    budget_max_lacs: '',
    preferred_locations: [],
    configuration: '',
    purpose: 'End-use'
  });
  
  // Notes state
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [showAllNotesDialog, setShowAllNotesDialog] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [customerNotes, setCustomerNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  
  // WhatsApp Smart Note state
  const [showWhatsAppDialog, setShowWhatsAppDialog] = useState(false);
  const [whatsAppMessage, setWhatsAppMessage] = useState('');
  const [whatsAppTone, setWhatsAppTone] = useState('Formal');
  const [whatsAppConverting, setWhatsAppConverting] = useState(false);
  const [lastSavedNote, setLastSavedNote] = useState('');
  const [fetchError, setFetchError] = useState(false);
  
  // Follow-up action dialog state (for reschedule/complete)
  const [showFollowupActionDialog, setShowFollowupActionDialog] = useState(false);
  const [selectedFollowup, setSelectedFollowup] = useState(null);
  const [followupActionForm, setFollowupActionForm] = useState({
    action: 'reschedule', // 'reschedule' or 'complete'
    new_date: '',
    new_time: '10:00',
    notes: '',
    outcome: 'Connected',
    outcome_remark: ''
  });
  // Fetch data
  useEffect(() => {
    fetchData();
  }, [customerId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setFetchError(false);
      const [customerRes, projectsRes, notesRes] = await Promise.all([
        api.getCustomer(customerId),
        api.getProjects(),
        api.getCustomerNotes(customerId, 50).catch(() => ({ data: { notes: [] } }))
      ]);
      if (!customerRes.data || !customerRes.data.customer_id) {
        setFetchError(true);
        setLoading(false);
        return;
      }
      setCustomer(customerRes.data);
      setProjects(projectsRes.data?.projects || []);
      setCustomerNotes(notesRes.data?.notes || []);
    } catch (error) {
      console.error('Failed to load customer:', error);
      setFetchError(true);
      // Don't navigate away - show error state instead
      if (error.response?.status === 403) {
        toast.error('Access denied. This customer belongs to another rep.');
      } else if (error.response?.status === 404) {
        toast.error('Customer not found');
      } else {
        toast.error('Failed to load customer data');
      }
    } finally {
      setLoading(false);
    }
  };

  // Computed values
  // Note: 'Won' and 'Lost' are system-controlled stages (from bookings/mark lost)
  // HWC (Hot/Warm/Cold) are rep-controlled temperature stages
  const isLost = customer?.stage === 'Lost';
  const isWon = customer?.stage === 'Won' || customer?.stage === 'Booked';
  const hasCompletedVisit = (customer?.total_visit_events || 0) >= 1;
  const canAddBooking = hasCompletedVisit && !isLost;
  const requirementEditsRemaining = 2 - (customer?.requirement_edit_count || 0);
  const canEditRequirement = requirementEditsRemaining > 0 && !isLost;

  // Lead health calculation
  const getLeadHealth = () => {
    if (!customer?.last_activity_at) return { text: 'No activity', color: 'text-slate-400' };
    const lastActivity = new Date(customer.last_activity_at);
    const now = new Date();
    const diffDays = Math.floor((now - lastActivity) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return { text: 'Active today', color: 'text-emerald-600', icon: '✓' };
    if (diffDays <= 3) return { text: `Active ${diffDays}d ago`, color: 'text-amber-600' };
    return { text: `Inactive ${diffDays}d`, color: 'text-red-500' };
  };

  // Format helpers
  const formatDateTime = (isoString) => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) + 
             ' • ' + date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return ''; }
  };

  const getStageConfig = (stage) => {
    return STAGE_OPTIONS.find(s => s.value === stage) || STAGE_OPTIONS[0];
  };

  // Populate edit form when customer data changes
  useEffect(() => {
    if (customer) {
      setEditCustomerForm({
        customer_name: customer.customer_name || '',
        customer_email: customer.customer_email || '',
        customer_phone: customer.customer_phone || '',
        budget_min_lacs: customer.budget_min_lacs || '',
        budget_max_lacs: customer.budget_max_lacs || '',
        preferred_locations: customer.preferred_locations || [],
        configuration: customer.configuration || '',
        purpose: customer.purpose || 'End-use'
      });
    }
  }, [customer]);

  // Handlers
  const handleStageChange = async (newStage) => {
    // Won/Lost are system-controlled, not manually changeable via dropdown
    if (isWon) {
      toast.error('Cannot change stage for won customers');
      return;
    }
    if (isLost) {
      toast.error('Cannot change stage for lost customers');
      return;
    }
    
    try {
      await api.updateCustomerStage(customerId, { stage: newStage });
      toast.success(`Stage updated to ${newStage}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update stage');
    }
  };

  const handleUpdateCustomer = async () => {
    try {
      await api.updateCustomer(customerId, {
        customer_name: editCustomerForm.customer_name,
        customer_email: editCustomerForm.customer_email,
        customer_phone: editCustomerForm.customer_phone,
        budget_min_lacs: editCustomerForm.budget_min_lacs ? parseFloat(editCustomerForm.budget_min_lacs) : null,
        budget_max_lacs: editCustomerForm.budget_max_lacs ? parseFloat(editCustomerForm.budget_max_lacs) : null,
        preferred_locations: editCustomerForm.preferred_locations,
        configuration: editCustomerForm.configuration,
        purpose: editCustomerForm.purpose
      });
      toast.success('Customer updated successfully');
      setShowEditCustomerDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update customer');
    }
  };

  const handleMarkLost = async () => {
    if (!lostForm.reason) {
      toast.error('Lost reason is required');
      return;
    }
    if (!lostForm.notes?.trim()) {
      toast.error('Final remark is required');
      return;
    }
    try {
      await api.markCustomerLost(customerId, { 
        lost_reason: lostForm.reason, 
        lost_remark: lostForm.notes 
      });
      toast.success('Submitted for Lost review');
      setShowLostDialog(false);
      setLostForm({ reason: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as lost');
    }
  };

  const handleScheduleVisit = async () => {
    // Validation: Project required for Site Visit, optional for OBM
    if (scheduleVisitForm.meeting_type === 'Site Visit' && !scheduleVisitForm.project_id) {
      toast.error('Please select a project for Site Visit');
      return;
    }
    if (scheduleVisitForm.meeting_type === 'OBM' && !scheduleVisitForm.obm_purpose) {
      toast.error('Please select a meeting purpose for OBM');
      return;
    }
    if (!scheduleVisitForm.visit_date || !scheduleVisitForm.visit_time) {
      toast.error('Please select date and time');
      return;
    }
    try {
      await api.scheduleVisit(customerId, {
        ...scheduleVisitForm,
        obm_purpose: scheduleVisitForm.meeting_type === 'OBM' ? scheduleVisitForm.obm_purpose : undefined
      });
      toast.success(`${scheduleVisitForm.meeting_type === 'OBM' ? 'OBM' : 'Site Visit'} scheduled!`);
      setShowScheduleVisitDialog(false);
      setScheduleVisitForm({ project_id: '', project_name: '', visit_date: '', visit_time: '10:00', notes: '', auto_reminder: true, meeting_type: 'Site Visit', obm_purpose: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule');
    }
  };

  const handleMarkVisitCompleted = async () => {
    if (!selectedVisit) return;
    
    // Check if token exists before making request
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Session expired. Please login again.');
      window.location.href = '/login';
      return;
    }
    
    try {
      await api.markVisitCompleted(selectedVisit.event_id, {
        project_id: completeVisitForm.project_id || selectedVisit.project_id,
        project_name: completeVisitForm.project_name || selectedVisit.project_name,
        completed_date: completeVisitForm.completed_date || new Date().toISOString().split('T')[0],
        completed_time: completeVisitForm.completed_time || new Date().toTimeString().slice(0, 5),
        remarks: completeVisitForm.remarks,
        next_followup_date: completeVisitForm.next_followup_date || undefined,
        next_followup_time: completeVisitForm.next_followup_time || undefined,
        lead_warmth: completeVisitForm.lead_warmth || undefined,
      });
      toast.success('Visit marked as completed');
      setShowCompleteVisitDialog(false);
      setSelectedVisit(null);
      setCompleteVisitForm({ project_id: '', project_name: '', completed_date: '', completed_time: '', remarks: '', next_followup_date: '', next_followup_time: '', lead_warmth: '' });
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to complete visit';
      // If auth error, prompt re-login
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        toast.error(errorMsg);
      }
    }
  };

  const handleCancelVisit = async () => {
    if (!selectedVisit || !cancelVisitForm.cancel_reason) {
      toast.error('Cancel reason is required');
      return;
    }
    try {
      await api.cancelVisit(selectedVisit.event_id, cancelVisitForm);
      toast.success('Visit cancelled');
      setShowCancelVisitDialog(false);
      setSelectedVisit(null);
      setCancelVisitForm({ cancel_reason: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel visit');
    }
  };

  const handleRescheduleVisit = async () => {
    if (!selectedVisit || !rescheduleForm.new_date || !rescheduleForm.new_time) {
      toast.error('New date and time are required');
      return;
    }
    try {
      await api.rescheduleVisit(selectedVisit.event_id, rescheduleForm);
      toast.success('Visit rescheduled');
      setShowRescheduleVisitDialog(false);
      setSelectedVisit(null);
      setRescheduleForm({ new_date: '', new_time: '10:00', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reschedule visit');
    }
  };

  const handleAddFollowup = async () => {
    if (!followupForm.due_date) {
      toast.error('Follow-up date is required');
      return;
    }
    try {
      await api.createFollowup(customerId, {
        due_datetime: `${followupForm.due_date}T${followupForm.due_time}:00`,
        notes: followupForm.notes || 'Follow-up scheduled',
        followup_type: followupForm.type,
      });
      toast.success('Follow-up added');
      setShowFollowupDialog(false);
      setFollowupForm({ due_date: '', due_time: '10:00', notes: '', type: 'Call follow-up' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add follow-up');
    }
  };

  const handleCompleteFollowup = async (followupId) => {
    if (!followupId) return;
    try {
      await api.completeFollowup(followupId, { 
        outcome: 'Completed',
        outcome_remark: 'Follow-up completed'
      });
      toast.success('Follow-up marked as completed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete follow-up');
    }
  };

  // Open follow-up action dialog (reschedule/complete) - like My Tasks
  const handleOpenFollowupAction = (followup) => {
    setSelectedFollowup(followup);
    // Pre-populate the form
    const dueDate = followup.due_datetime ? followup.due_datetime.split('T')[0] : '';
    const dueTime = followup.due_datetime && followup.due_datetime.length > 11 
      ? followup.due_datetime.substring(11, 16) 
      : '10:00';
    setFollowupActionForm({
      action: 'reschedule',
      new_date: dueDate,
      new_time: dueTime,
      notes: followup.notes || '',
      outcome: 'Connected',
      outcome_remark: ''
    });
    setShowFollowupActionDialog(true);
  };

  // Submit follow-up action (reschedule or complete)
  const handleSubmitFollowupAction = async () => {
    if (!selectedFollowup) return;
    
    try {
      if (followupActionForm.action === 'reschedule') {
        // Validate reschedule form
        if (!followupActionForm.new_date) {
          toast.error('Please select a new date');
          return;
        }
        
        const newDatetime = `${followupActionForm.new_date}T${followupActionForm.new_time}:00`;
        await api.rescheduleFollowup(selectedFollowup.followup_id, {
          due_datetime: newDatetime,
          notes: followupActionForm.notes
        });
        toast.success('Follow-up rescheduled - also updated in My Tasks');
      } else {
        // Complete follow-up
        await api.completeFollowup(selectedFollowup.followup_id, {
          outcome: followupActionForm.outcome,
          outcome_remark: followupActionForm.outcome_remark || 'Follow-up completed'
        });
        toast.success('Follow-up completed - also updated in My Tasks');
      }
      
      // Close dialog and refresh
      setShowFollowupActionDialog(false);
      setSelectedFollowup(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update follow-up');
    }
  };

  const handleAddNote = async () => {
    if (!noteContent.trim()) {
      toast.error('Note content is required');
      return;
    }
    try {
      setNotesLoading(true);
      const savedNote = noteContent.trim();
      await api.addCustomerNote(customerId, { content: savedNote });
      toast.success('Note added');
      setShowNotesDialog(false);
      setNoteContent('');
      setLastSavedNote(savedNote);
      // Refresh notes
      const notesRes = await api.getCustomerNotes(customerId, 50);
      setCustomerNotes(notesRes.data?.notes || []);
      fetchData(); // Refresh timeline
      
      // Show WhatsApp conversion option if note is substantial enough
      if (savedNote.split(' ').length >= 5) {
        setShowWhatsAppDialog(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add note');
    } finally {
      setNotesLoading(false);
    }
  };

  const handleConvertToWhatsApp = async () => {
    if (!lastSavedNote) {
      toast.error('No note to convert');
      return;
    }
    try {
      setWhatsAppConverting(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const repName = user.full_name || user.rep_name || 'Your Representative';
      
      const response = await api.convertNoteToWhatsApp({
        customer_name: customer?.customer_name || 'Customer',
        rep_name: repName,
        note_content: lastSavedNote,
        tone_type: whatsAppTone
      });
      
      if (response.data.success) {
        setWhatsAppMessage(response.data.whatsapp_message);
      } else {
        toast.error(response.data.message || 'Could not convert note');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert note');
    } finally {
      setWhatsAppConverting(false);
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(whatsAppMessage);
      toast.success('Copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const handleShareViaWhatsApp = () => {
    const phone = customer?.customer_phone?.replace(/\D/g, '') || '';
    const encodedMessage = encodeURIComponent(whatsAppMessage);
    const whatsAppUrl = phone 
      ? `https://wa.me/91${phone}?text=${encodedMessage}`
      : `https://wa.me/?text=${encodedMessage}`;
    window.open(whatsAppUrl, '_blank');
  };

  const handleAddBooking = async () => {
    if (!bookingForm.project_id || !bookingForm.agreement_value) {
      toast.error('Project and agreement value are required');
      return;
    }
    try {
      // Convert lacs to actual value (1 lac = 100000)
      const bookingValueRupees = parseFloat(bookingForm.agreement_value) * 100000;
      
      await api.createBookingFromCRM(customerId, {
        project_id: bookingForm.project_id,
        project_name: bookingForm.project_name,
        booking_value: bookingValueRupees,
        booking_date: bookingForm.booking_date || new Date().toISOString().split('T')[0],
        notes: bookingForm.remarks,
      });
      toast.success('Booking created successfully');
      setShowBookingDialog(false);
      setBookingForm({ project_id: '', project_name: '', agreement_value: '', booking_date: '', remarks: '' });
      fetchData();
    } catch (error) {
      // Handle Pydantic validation errors (array of error objects) or string errors
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        // Pydantic returns array of errors - extract first message
        const firstError = detail[0];
        toast.error(firstError?.msg || 'Validation error');
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to create booking');
      }
    }
  };

  const handleUpdateRequirement = async () => {
    try {
      await api.updateCustomer(customerId, {
        budget_min_lacs: requirementForm.budgetMin ? parseFloat(requirementForm.budgetMin) : null,
        budget_max_lacs: requirementForm.budgetMax ? parseFloat(requirementForm.budgetMax) : null,
        preferred_locations: requirementForm.location ? [requirementForm.location] : null,
        purpose: requirementForm.purpose || null,
      });
      toast.success('Requirements updated');
      setShowRequirementDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update requirements');
    }
  };

  const handleAddReferral = async () => {
    if (!referralForm.name || !referralForm.phone) {
      toast.error('Name and phone are required');
      return;
    }
    if (referralForm.phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    try {
      await api.addReferralLead(customerId, {
        name: referralForm.name,
        phone: referralForm.phone,
        email: referralForm.email || undefined,
      });
      toast.success('Referral added successfully');
      setShowReferralDialog(false);
      setReferralForm({ name: '', phone: '', email: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add referral');
    }
  };

  // Open dialogs with pre-filled data
  const openCompleteVisitDialog = (visit) => {
    setSelectedVisit(visit);
    
    // Pre-populate with scheduled visit date/time (user can edit if actual completion time differs)
    let completedDate = new Date().toISOString().split('T')[0];
    let completedTime = new Date().toTimeString().slice(0, 5);
    
    // If visit has a scheduled datetime, use that as default (editable)
    if (visit.visit_datetime) {
      try {
        const visitDate = new Date(visit.visit_datetime);
        if (!isNaN(visitDate.getTime())) {
          completedDate = visitDate.toISOString().split('T')[0];
          completedTime = visitDate.toTimeString().slice(0, 5);
        }
      } catch (e) {
        // Fall back to current time if parsing fails
      }
    }
    
    setCompleteVisitForm({
      project_id: visit.project_id,
      project_name: visit.project_name,
      completed_date: completedDate,
      completed_time: completedTime,
      remarks: visit.remarks || '',
      next_followup_date: '',
      next_followup_time: '',
    });
    setShowCompleteVisitDialog(true);
  };

  const openCancelVisitDialog = (visit) => {
    setSelectedVisit(visit);
    setCancelVisitForm({ cancel_reason: '', notes: '' });
    setShowCancelVisitDialog(true);
  };

  const openRescheduleDialog = (visit) => {
    setSelectedVisit(visit);
    setRescheduleForm({ 
      new_date: visit.visit_datetime?.split('T')[0] || '', 
      new_time: visit.visit_datetime?.split('T')[1]?.slice(0, 5) || '10:00', 
      notes: '' 
    });
    setShowRescheduleVisitDialog(true);
  };

  const openRequirementDialog = () => {
    setRequirementForm({
      budgetMin: customer?.budget_min_lacs || '',
      budgetMax: customer?.budget_max_lacs || '',
      location: customer?.preferred_locations?.[0] || customer?.preferred_location || '',
      purpose: customer?.purpose || '',
    });
    setShowRequirementDialog(true);
  };

  // Helper functions for activity icons and colors (must be defined before useMemo)
  const getActivityIcon = (type) => {
    switch (type) {
      case 'visit_scheduled': return CalendarClock;
      case 'obm_scheduled': return CalendarClock;
      case 'visit_completed': case 'visit': return CheckCircle;
      case 'obm_completed': return CheckCircle;
      case 'obm_gift_completed': return Gift;
      case 'visit_cancelled': return X;
      case 'followup': case 'followup_created': case 'followup_done': return Phone;
      case 'booking': case 'booking_created': return DollarSign;
      case 'lost': return AlertCircle;
      case 'requirement_updated': return Edit;
      case 'note_added': return StickyNote;
      case 'stage_change': return Activity;
      default: return Activity;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'visit_scheduled': return 'text-purple-600 bg-purple-50';
      case 'obm_scheduled': return 'text-orange-600 bg-orange-50';
      case 'visit_completed': case 'visit': return 'text-emerald-600 bg-emerald-50';
      case 'obm_completed': return 'text-amber-600 bg-amber-50';
      case 'obm_gift_completed': return 'text-pink-600 bg-pink-50';
      case 'visit_cancelled': return 'text-red-500 bg-red-50';
      case 'followup': case 'followup_created': case 'followup_done': return 'text-blue-600 bg-blue-50';
      case 'booking': case 'booking_created': return 'text-amber-600 bg-amber-50';
      case 'lost': return 'text-slate-500 bg-slate-100';
      case 'note_added': return 'text-cyan-600 bg-cyan-50';
      case 'stage_change': return 'text-orange-600 bg-orange-50';
      default: return 'text-slate-600 bg-slate-50';
    }
  };

  // Get activity emoji for better readability
  const getActivityEmoji = (type, metadata) => {
    switch (type) {
      case 'visit_scheduled': return '🏗️';
      case 'obm_scheduled': return '☕';
      case 'visit_completed': case 'visit': return '🏗️';
      case 'obm_completed': return '☕';
      case 'obm_gift_completed': return '🎁';
      case 'visit_cancelled': return '❌';
      case 'followup': case 'followup_created': case 'followup_done': return '📞';
      case 'booking': case 'booking_created': return '💰';
      case 'note_added': return '📝';
      case 'stage_change': return '🔄';
      default: return '📋';
    }
  };

  // Activity data
  const activityItems = useMemo(() => {
    if (!customer) return [];
    const items = [];
    
    // Add timeline events
    (customer.timeline || []).forEach(event => {
      items.push({
        type: event.event_type,
        title: event.title,
        description: event.description,
        datetime: event.event_datetime,
        icon: getActivityIcon(event.event_type),
        color: getActivityColor(event.event_type),
        emoji: getActivityEmoji(event.event_type, event.metadata),
        metadata: event.metadata
      });
    });
    
    return items.sort((a, b) => new Date(b.datetime) - new Date(a.datetime));
  }, [customer]);

  // Notes-only filtered view
  const notesItems = useMemo(() => {
    return activityItems.filter(item => item.type === 'note_added');
  }, [activityItems]);

  // Project-related activity only
  const projectActivityItems = useMemo(() => {
    return activityItems.filter(item => 
      ['visit_scheduled', 'visit_completed', 'visit', 'visit_cancelled', 'booking', 'booking_created'].includes(item.type)
    );
  }, [activityItems]);

  const projectGroupedVisits = useMemo(() => {
    if (!customer?.project_wise_visits) return [];
    return customer.project_wise_visits;
  }, [customer]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // Error state - customer not found, access denied, or fetch failed
  if (!customer || fetchError) {
    return (
      <div className="min-h-screen bg-slate-50">
        <BreadcrumbNav 
          items={[
            { label: 'CRM', path: '/crm' },
            { label: 'Error' }
          ]}
          fallbackPath="/crm"
        />
        <div className="max-w-lg mx-auto mt-20 p-6 bg-white rounded-xl shadow-lg text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-slate-800 mb-2">Customer Not Found</h2>
          <p className="text-slate-500 mb-6">
            The customer you're looking for doesn't exist or you don't have permission to view it.
          </p>
          <Button 
            onClick={() => navigate('/crm')}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            ← Back to CRM
          </Button>
        </div>
      </div>
    );
  }

  const leadHealth = getLeadHealth();
  const stageConfig = getStageConfig(customer.stage);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Breadcrumb Navigation */}
      <BreadcrumbNav 
        items={[
          { label: 'CRM', path: '/crm' },
          { label: customer.customer_name }
        ]}
        fallbackPath="/crm"
      />
      
      {/* Header */}
      <div className="sticky top-[52px] z-10 bg-white border-b border-slate-200 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <UserAvatar name={customer.customer_name} size="md" />
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-semibold text-lg text-slate-900" data-testid="customer-name">
                  {customer.customer_name}
                </h1>
                {/* Stage Display - Won/Lost are fixed badges, HWC is dropdown */}
                {isWon ? (
                  <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                    🏆 Won
                  </span>
                ) : isLost ? (
                  <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-slate-200 text-slate-600">
                    Lost
                  </span>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button 
                        className={`px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${stageConfig.color} hover:opacity-80 transition-opacity`}
                        data-testid="stage-dropdown"
                      >
                        {customer.stage || 'Warm'}
                        <ChevronDown size={12} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      {STAGE_OPTIONS.map(stage => (
                        <DropdownMenuItem 
                          key={stage.value}
                          onClick={() => handleStageChange(stage.value)}
                          className={customer.stage === stage.value ? 'bg-purple-50' : ''}
                        >
                          <span className={`w-2 h-2 rounded-full mr-2 ${stage.color.split(' ')[0]}`}></span>
                          {stage.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                {/* Retargeting Lead Tag - differentiate between Lost Lead Recovery and VDNB */}
                {customer.is_retargeting_lead && (
                  <TooltipProvider delayDuration={100}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold flex items-center gap-1 cursor-help border ${
                          customer.retargeting_type === 'VDNB'
                            ? 'bg-purple-100 text-purple-700 border-purple-200'
                            : 'bg-orange-100 text-orange-700 border-orange-200'
                        }`}>
                          <RotateCcw size={10} />
                          {customer.retargeting_type === 'VDNB' ? 'VDNB' : 'Retargeting'}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="bg-slate-800 text-white p-2.5 max-w-[220px]">
                        <div className="text-[11px] space-y-1.5">
                          <p className={`font-semibold text-xs ${customer.retargeting_type === 'VDNB' ? 'text-purple-300' : 'text-orange-300'}`}>
                            {customer.retargeting_type === 'VDNB' ? 'VDNB Recovery' : 'Lost Lead Recovery'}
                          </p>
                          {customer.original_rep_name && (
                            <p>Original Rep: <span className="font-medium">{customer.original_rep_name}</span></p>
                          )}
                          {(customer.reassigned_from_lost_by_name || customer.reassigned_from_vdnb_by_name) && (
                            <p>Reassigned By: <span className="font-medium">{customer.reassigned_from_lost_by_name || customer.reassigned_from_vdnb_by_name}</span></p>
                          )}
                          {(customer.reassigned_from_lost_at || customer.reassigned_from_vdnb_at) && (
                            <p>Date: <span className="font-medium">{new Date(customer.reassigned_from_lost_at || customer.reassigned_from_vdnb_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span></p>
                          )}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              {/* Customer Email */}
              {customer.customer_email && (
                <p className="text-xs text-slate-500 mt-0.5" data-testid="customer-email">
                  {customer.customer_email}
                </p>
              )}
              <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                <span className={leadHealth.color}>
                  {leadHealth.icon && <span className="mr-1">{leadHealth.icon}</span>}
                  Lead Health: {leadHealth.text}
                </span>
              </div>
            </div>
          </div>
          
          {/* Right side actions */}
          <div className="flex items-center gap-1">
            {/* 3-dot menu - Rep actions only (no Reassign/Delete) */}
            {!isLost && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-2 hover:bg-slate-100 rounded-lg" data-testid="more-options">
                    <MoreVertical size={20} className="text-slate-600" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setShowLostDialog(true)}>
                    <AlertCircle size={14} className="mr-2" /> Mark as Lost
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowEditCustomerDialog(true)}>
                    <Edit size={14} className="mr-2" /> Edit Customer
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <MobileMenuTrigger user={user} />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
        {/* Primary Action Buttons - Mobile: 2x2 grid (no scroll), Desktop: single row */}
        {!isLost && (
          <Card className="p-3 sm:p-4 bg-white rounded-2xl shadow-sm border-0">
            {/* Mobile: 2x2 Grid - Fixed layout, no overflow */}
            <div className="grid grid-cols-2 gap-2.5 md:hidden">
              <Button 
                onClick={() => setShowScheduleVisitDialog(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white rounded-xl h-12 text-sm font-medium flex items-center justify-center"
                data-testid="schedule-meeting-btn"
              >
                <CalendarClock size={18} className="mr-2 flex-shrink-0" />
                <span>Schedule Meeting</span>
              </Button>
              <Button 
                onClick={() => navigate(`/bookings/new?customer_id=${customerId}&customer_email=${encodeURIComponent(customer.customer_email || '')}&customer_name=${encodeURIComponent(customer.customer_name || '')}`)}
                variant="outline"
                className={`rounded-xl h-12 text-sm font-medium flex items-center justify-center ${canAddBooking ? 'border-emerald-200 text-emerald-700 hover:bg-emerald-50' : 'border-slate-200 text-slate-400 cursor-not-allowed'}`}
                disabled={!canAddBooking}
                data-testid="add-booking-btn-mobile"
              >
                <DollarSign size={18} className="mr-2 flex-shrink-0" />
                <span>Add Booking</span>
              </Button>
              <Button 
                onClick={() => setShowFollowupDialog(true)}
                variant="outline"
                className="border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl h-12 text-sm font-medium flex items-center justify-center"
                data-testid="add-followup-btn-mobile"
              >
                <Phone size={18} className="mr-2 flex-shrink-0" />
                <span>Add Follow-up</span>
              </Button>
              <Button 
                onClick={() => setShowNotesDialog(true)}
                variant="outline"
                className="border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl h-12 text-sm font-medium flex items-center justify-center"
                data-testid="add-notes-btn-mobile"
              >
                <StickyNote size={18} className="mr-2 flex-shrink-0" />
                <span>Add Notes</span>
              </Button>
            </div>
            
            {/* SAIRA Lead Context Button - Full width below grid on mobile */}
            <div className="mt-2.5 md:hidden">
              <Button 
                onClick={() => navigate(`/saira?lead_id=${customerId}&lead_name=${encodeURIComponent(customer.customer_name || '')}&project=${encodeURIComponent(customer.first_project_name || '')}&stage=${encodeURIComponent(customer.lead_stage || '')}`)}
                variant="outline"
                className="w-full border-purple-200 text-purple-700 hover:bg-purple-50 rounded-xl h-12 text-sm font-medium flex items-center justify-center"
                data-testid="ask-saira-btn-mobile"
              >
                <Sparkles size={18} className="mr-2 flex-shrink-0" />
                <span>Ask SAIRA about this Lead</span>
              </Button>
            </div>
            
            {/* Desktop: Single row */}
            <div className="hidden md:flex gap-2">
              <Button 
                onClick={() => setShowScheduleVisitDialog(true)}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white rounded-xl h-11"
                data-testid="schedule-meeting-btn"
              >
                <CalendarClock size={18} className="mr-2" />
                Schedule Meeting
              </Button>
              <Button 
                onClick={() => setShowFollowupDialog(true)}
                variant="outline"
                className="flex-1 border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl h-11"
                data-testid="add-followup-btn"
              >
                <Phone size={18} className="mr-2" />
                Add Follow-up
              </Button>
              <Button 
                onClick={() => navigate(`/bookings/new?customer_id=${customerId}&customer_email=${encodeURIComponent(customer.customer_email || '')}&customer_name=${encodeURIComponent(customer.customer_name || '')}`)}
                variant="outline"
                className={`flex-1 border-slate-200 rounded-xl h-11 ${canAddBooking ? 'text-emerald-700 hover:bg-emerald-50 border-emerald-200' : 'text-slate-400 cursor-not-allowed'}`}
                disabled={!canAddBooking}
                data-testid="add-booking-btn"
              >
                <DollarSign size={18} className="mr-2" />
                Add Booking
              </Button>
              <Button 
                onClick={() => setShowNotesDialog(true)}
                variant="outline"
                className="flex-1 border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl h-11"
                data-testid="add-notes-btn"
              >
                <StickyNote size={18} className="mr-2" />
                Add Notes
              </Button>
              <Button 
                onClick={() => navigate(`/saira?lead_id=${customerId}&lead_name=${encodeURIComponent(customer.customer_name || '')}&project=${encodeURIComponent(customer.first_project_name || '')}&stage=${encodeURIComponent(customer.lead_stage || '')}`)}
                variant="outline"
                className="flex-1 border-purple-200 text-purple-700 hover:bg-purple-50 rounded-xl h-11"
                data-testid="ask-saira-btn"
              >
                <Sparkles size={18} className="mr-2" />
                Ask SAIRA about this Lead
              </Button>
            </div>
            
            {!hasCompletedVisit && (
              <p className="text-xs text-slate-400 mt-2 text-center">
                Complete at least 1 visit to enable booking
              </p>
            )}
          </Card>
        )}

        {/* Lost Banner */}
        {isLost && (
          <Card className="p-4 bg-slate-100 rounded-2xl border-0">
            <div className="flex items-center gap-3">
              <AlertCircle size={20} className="text-slate-500" />
              <div>
                <p className="font-medium text-slate-700">Customer marked as Lost</p>
                <p className="text-xs text-slate-500">{customer.lost_reason || 'No reason provided'}</p>
              </div>
            </div>
          </Card>
        )}

        {/* Upcoming Meetings Section */}
        <Card className="bg-white rounded-2xl shadow-sm border-0 overflow-hidden">
          <div className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <CalendarClock size={18} className="text-purple-600" />
              <h2 className="font-semibold text-slate-900">Upcoming Meetings</h2>
            </div>
            
            {customer.scheduled_visits?.length > 0 ? (
              <div className="space-y-3">
                {customer.scheduled_visits.map(visit => (
                  <div key={visit.event_id} className={`p-3 rounded-xl border ${
                    visit.meeting_type === 'OBM' 
                      ? 'bg-amber-50 border-amber-100' 
                      : 'bg-purple-50 border-purple-100'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        {/* Meeting Type Badge */}
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={`text-xs px-2 py-0.5 ${
                            visit.meeting_type === 'OBM'
                              ? 'bg-amber-100 text-amber-700 border-amber-200'
                              : 'bg-purple-100 text-purple-700 border-purple-200'
                          }`}>
                            {visit.meeting_type === 'OBM' ? '☕ OBM' : '🏗️ Site Visit'}
                          </Badge>
                        </div>
                        {/* Project or OBM Purpose */}
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                          <p className="font-medium text-slate-900">
                            {visit.meeting_type === 'OBM' 
                              ? (visit.obm_purpose || 'Outbound Meeting')
                              : visit.project_name
                            }
                          </p>
                        </div>
                        {/* Show project for OBM if available */}
                        {visit.meeting_type === 'OBM' && visit.project_name && (
                          <p className="text-xs text-slate-500 mt-0.5 ml-4">Project: {visit.project_name}</p>
                        )}
                        <p className="text-sm text-slate-600 mt-1">{formatDateTime(visit.visit_datetime)}</p>
                        {visit.remarks && <p className="text-xs text-slate-500 mt-1">{visit.remarks}</p>}
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button
                        size="sm"
                        onClick={() => openCompleteVisitDialog(visit)}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg h-9"
                        data-testid="mark-completed-btn"
                      >
                        <CheckCircle size={14} className="mr-1.5" />
                        Mark Completed
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openCancelVisitDialog(visit)}
                        className="border-slate-200 text-slate-600 hover:bg-slate-50 rounded-lg h-9 px-3"
                        data-testid="cancel-visit-btn"
                      >
                        <X size={14} className="mr-1" />
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openRescheduleDialog(visit)}
                        className="border-slate-200 text-slate-600 hover:bg-slate-50 rounded-lg h-9 px-3"
                        data-testid="reschedule-btn"
                      >
                        <RefreshCw size={14} className="mr-1" />
                        Reschedule
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 bg-slate-50 rounded-xl">
                <CalendarClock size={24} className="mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">No meetings scheduled</p>
                {!isLost && (
                  <Button
                    variant="link"
                    onClick={() => setShowScheduleVisitDialog(true)}
                    className="text-purple-600 text-sm mt-1"
                  >
                    Schedule a meeting →
                  </Button>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Requirements Section */}
        <Card className="bg-white rounded-2xl shadow-sm border-0 overflow-hidden">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Home size={18} className="text-purple-600" />
                <h2 className="font-semibold text-slate-900">Requirements</h2>
              </div>
              {canEditRequirement ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={openRequirementDialog}
                  className="text-purple-600 hover:bg-purple-50 h-8"
                  data-testid="edit-requirement-btn"
                >
                  <Edit size={14} className="mr-1" />
                  Edit ({requirementEditsRemaining}/2 remaining)
                </Button>
              ) : requirementEditsRemaining <= 0 ? (
                <span className="text-xs text-slate-400 flex items-center gap-1">
                  <AlertCircle size={12} /> Edit limit reached
                </span>
              ) : null}
            </div>
            
            {(customer.budget_min_lacs || customer.budget_max_lacs || customer.preferred_locations?.length > 0 || customer.purpose) ? (
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Budget</p>
                  <p className="font-medium text-slate-900">
                    {customer.budget_min_lacs || customer.budget_max_lacs 
                      ? `₹${customer.budget_min_lacs || 0}-${customer.budget_max_lacs || 0} Lacs`
                      : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Location</p>
                  <p className="font-medium text-slate-900">
                    {customer.preferred_locations?.length > 0 
                      ? customer.preferred_locations.join(', ')
                      : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Purpose</p>
                  <p className="font-medium text-slate-900">{customer.purpose || '—'}</p>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 bg-slate-50 rounded-xl">
                <FileText size={24} className="mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">No requirement added</p>
                {canEditRequirement && (
                  <Button
                    variant="link"
                    onClick={openRequirementDialog}
                    className="text-purple-600 text-sm mt-1"
                  >
                    Add Requirement →
                  </Button>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Referral Section (Two-Way) */}
        <Card className="bg-white rounded-2xl shadow-sm border-0 overflow-hidden">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Gift size={18} className="text-purple-600" />
                <h2 className="font-semibold text-slate-900">Referral</h2>
                {customer.referrals_given?.length > 0 && (
                  <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                    {customer.referrals_given.length} referral{customer.referrals_given.length !== 1 ? 's' : ''}
                  </Badge>
                )}
              </div>
              {!isLost && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowReferralDialog(true)}
                  className="text-purple-600 hover:bg-purple-50 h-8"
                  data-testid="add-referral-btn"
                >
                  <Plus size={14} className="mr-1" />
                  Add Referral
                </Button>
              )}
            </div>
            
            <div className="space-y-4">
              {/* Referred By */}
              <div>
                <p className="text-xs font-medium text-slate-500 mb-2 uppercase tracking-wide">Referred By</p>
                {customer.referred_by ? (
                  <div className="flex items-center justify-between p-3 bg-amber-50 rounded-xl border border-amber-100">
                    <div className="flex items-center gap-3">
                      <UserAvatar name={customer.referred_by.name} size="sm" />
                      <div>
                        <p className="font-medium text-slate-900">{customer.referred_by.name}</p>
                        <p className="text-xs text-slate-500">{customer.referred_by.type} • {formatDate(customer.referred_by.date)}</p>
                      </div>
                    </div>
                    <Badge className={`${customer.referred_by.status === 'Paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                      {customer.referred_by.status || 'Eligible'}
                    </Badge>
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 py-2">No inbound referral</p>
                )}
              </div>
              
              {/* Referrals Given */}
              <div>
                <p className="text-xs font-medium text-slate-500 mb-2 uppercase tracking-wide">Referrals Given</p>
                {customer.referrals_given?.length > 0 ? (
                  <div className="space-y-2">
                    {customer.referrals_given.slice(0, 3).map((ref, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm text-slate-900">{ref.name}</p>
                          <p className="text-xs text-slate-500">{ref.stage || 'New'}</p>
                        </div>
                        {ref.agreement_value && (
                          <span className="text-xs font-medium text-emerald-600">₹{ref.agreement_value?.toLocaleString()}</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 py-2">No referrals given yet</p>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Engagement Summary Strip */}
        <Card className="bg-white rounded-2xl shadow-sm border-0 overflow-hidden">
          <div className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Activity size={18} className="text-purple-600" />
              <h2 className="font-semibold text-slate-900">Engagement Summary</h2>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div className="p-3 bg-purple-50 rounded-xl text-center">
                <p className="text-2xl font-bold text-purple-700">{customer.total_visit_events || 0}</p>
                <p className="text-xs text-slate-600">Total Visits</p>
              </div>
              <div className="p-3 bg-cyan-50 rounded-xl text-center">
                <p className="text-2xl font-bold text-cyan-700">{customer.revisit_count || 0}</p>
                <p className="text-xs text-slate-600">Revisits</p>
              </div>
              <div 
                className="p-3 bg-amber-50 rounded-xl text-center cursor-pointer hover:bg-amber-100 transition-colors"
                onClick={() => customer.pending_followups?.length > 0 && setShowFollowupsExpanded(!showFollowupsExpanded)}
                data-testid="followups-pending-card"
              >
                <p className="text-2xl font-bold text-amber-700">{customer.pending_followups?.length || 0}</p>
                <p className="text-xs text-slate-600">Follow-ups Pending</p>
                {customer.pending_followups?.length > 0 && (
                  <p className="text-xs text-amber-600 mt-1">Tap to view →</p>
                )}
              </div>
              <div className="p-3 bg-slate-100 rounded-xl text-center">
                <p className="text-2xl font-bold text-slate-700">{customer.unique_projects || 0}</p>
                <p className="text-xs text-slate-600">Projects Explored</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Pending Follow-ups Section (expandable) */}
        {showFollowupsExpanded && customer.pending_followups?.length > 0 && (
          <Card className="bg-white rounded-2xl shadow-sm border-0 overflow-hidden border-2 border-amber-200">
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Phone size={18} className="text-amber-600" />
                  <h2 className="font-semibold text-slate-900">Pending Follow-ups</h2>
                  <Badge className="bg-amber-100 text-amber-700 text-xs">
                    {customer.pending_followups.length}
                  </Badge>
                </div>
                <button
                  onClick={() => setShowFollowupsExpanded(false)}
                  className="text-xs text-slate-500 hover:text-slate-700"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="space-y-2">
                {customer.pending_followups.map((followup, idx) => {
                  const dueDate = followup.due_datetime ? new Date(followup.due_datetime) : null;
                  const isOverdue = dueDate && dueDate < new Date();
                  const isToday = dueDate && dueDate.toDateString() === new Date().toDateString();
                  
                  return (
                    <div 
                      key={followup.followup_id || idx} 
                      className={`p-3 rounded-xl border cursor-pointer hover:shadow-md transition-all ${isOverdue ? 'bg-red-50 border-red-200 hover:border-red-300' : isToday ? 'bg-amber-50 border-amber-200 hover:border-amber-300' : 'bg-slate-50 border-slate-200 hover:border-slate-300'}`}
                      onClick={() => handleOpenFollowupAction(followup)}
                      data-testid={`followup-item-${followup.followup_id || idx}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Badge className={`text-xs ${isOverdue ? 'bg-red-100 text-red-700' : isToday ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                              {followup.followup_type || 'Follow-up'}
                            </Badge>
                            {isOverdue && <span className="text-xs text-red-600 font-medium">⚠ Overdue</span>}
                            {isToday && !isOverdue && <span className="text-xs text-amber-600 font-medium">📅 Due Today</span>}
                            {followup.linked_task_id && (
                              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200">
                                📌 Synced
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-700 mt-1">{followup.notes || 'No notes'}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            Due: {dueDate ? formatDateTime(followup.due_datetime) : 'Not set'}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-slate-400 mr-1">Tap to manage</span>
                          <CheckCircle size={18} className="text-slate-300" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        )}

        {/* Booking Section (visible only if booking exists) */}
        {customer.linked_booking && (
          <Card className="bg-white rounded-2xl shadow-sm border-0 overflow-hidden">
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Briefcase size={18} className="text-purple-600" />
                  <h2 className="font-semibold text-slate-900">Booking</h2>
                </div>
                <button
                  onClick={() => navigate(`/bookings/${customer.linked_booking.booking_id}`)}
                  className="text-xs text-purple-600 hover:text-purple-700 font-medium"
                >
                  View Details →
                </button>
              </div>
              
              <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-100">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-slate-900">{customer.linked_booking.project_name}</p>
                  <Badge className="bg-emerald-100 text-emerald-700">Booked</Badge>
                </div>
                <div className="grid grid-cols-3 gap-4 mt-3">
                  <div>
                    <p className="text-xs text-slate-500">Agreement Value</p>
                    <p className="font-semibold text-emerald-700">₹{customer.linked_booking.agreement_value?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Booking Date</p>
                    <p className="font-medium text-slate-900">{formatDate(customer.linked_booking.booking_date)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Status</p>
                    <p className="font-medium text-slate-900">{customer.linked_booking.status || 'Active'}</p>
                  </div>
                </div>
                
                {/* Document Links */}
                {(customer.linked_booking.cost_sheet_url || customer.linked_booking.booking_form_url) && (
                  <div className="mt-3 pt-3 border-t border-emerald-100">
                    <p className="text-xs text-slate-500 mb-2">Documents</p>
                    <div className="flex flex-wrap gap-2">
                      {customer.linked_booking.cost_sheet_url && (
                        <a
                          href={customer.linked_booking.cost_sheet_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 px-2 py-1 bg-purple-100 text-purple-700 rounded-lg text-xs hover:bg-purple-200"
                        >
                          <FileSpreadsheet size={12} />
                          Cost Sheet
                        </a>
                      )}
                      {customer.linked_booking.booking_form_url && (
                        <a
                          href={customer.linked_booking.booking_form_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 px-2 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs hover:bg-blue-200"
                        >
                          <FileText size={12} />
                          Booking Form
                        </a>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Upload Documents Link */}
                <div className="mt-3 pt-3 border-t border-emerald-100">
                  <button
                    onClick={() => navigate(`/bookings/${customer.linked_booking.booking_id}`)}
                    className="text-xs text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                  >
                    <Upload size={12} />
                    Upload / Manage Documents
                  </button>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Notes Card */}
        <Card className="bg-white rounded-2xl shadow-sm border-0 overflow-hidden">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <StickyNote size={18} className="text-amber-600" />
                <h2 className="font-semibold text-slate-900">Notes</h2>
                <Badge className="bg-amber-100 text-amber-700 text-xs ml-1">
                  {customerNotes.length}
                </Badge>
              </div>
              {customerNotes.length > 3 && (
                <button
                  onClick={() => setShowAllNotesDialog(true)}
                  className="text-xs text-purple-600 hover:text-purple-700 font-medium"
                >
                  View all
                </button>
              )}
            </div>
            
            {customerNotes.length > 0 ? (
              <div className="space-y-2">
                {customerNotes.slice(0, 3).map((note, idx) => (
                  <div key={note.note_id || idx} className="p-3 bg-amber-50/50 rounded-xl border border-amber-100">
                    <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">{note.content}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                      <span>{note.created_by_name}</span>
                      <span>•</span>
                      <span>{formatDateTime(note.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 bg-slate-50 rounded-xl">
                <StickyNote size={24} className="mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">No notes yet</p>
                <Button
                  onClick={() => setShowNotesDialog(true)}
                  variant="link"
                  className="text-purple-600 hover:text-purple-700 text-sm mt-1 h-auto p-0"
                >
                  Add your first note
                </Button>
              </div>
            )}
          </div>
        </Card>

        {/* Activity Section (Merged View) */}
        <Card className="bg-white rounded-2xl shadow-sm border-0 overflow-hidden">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <FileText size={18} className="text-purple-600" />
                <h2 className="font-semibold text-slate-900">Activity</h2>
              </div>
              {/* Tabs - Added Notes tab */}
              <div className="flex bg-slate-100 rounded-lg p-0.5">
                <button
                  onClick={() => setActiveActivityTab('all')}
                  className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    activeActivityTab === 'all' ? 'bg-white text-purple-700 shadow-sm' : 'text-slate-600'
                  }`}
                >
                  All Activity
                </button>
                <button
                  onClick={() => setActiveActivityTab('project')}
                  className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    activeActivityTab === 'project' ? 'bg-white text-purple-700 shadow-sm' : 'text-slate-600'
                  }`}
                >
                  Project View
                </button>
                <button
                  onClick={() => setActiveActivityTab('notes')}
                  className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    activeActivityTab === 'notes' ? 'bg-white text-purple-700 shadow-sm' : 'text-slate-600'
                  }`}
                >
                  Notes
                </button>
              </div>
            </div>
            
            {activeActivityTab === 'all' ? (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {activityItems.length > 0 ? (
                  activityItems.map((item, idx) => {
                    const IconComponent = item.icon;
                    return (
                      <div key={idx} className="flex gap-3 p-2.5 hover:bg-slate-50 rounded-lg transition-colors">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${item.color}`}>
                          <IconComponent size={14} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900">
                            <span className="mr-1">{item.emoji}</span>
                            {item.title}
                          </p>
                          {item.description && <p className="text-xs text-slate-500 mt-0.5">{item.description}</p>}
                          <p className="text-xs text-slate-400 mt-0.5">{formatDateTime(item.datetime)}</p>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8">
                    <Activity size={24} className="mx-auto text-slate-300 mb-2" />
                    <p className="text-sm text-slate-500">No activity yet</p>
                  </div>
                )}
              </div>
            ) : activeActivityTab === 'notes' ? (
              /* Notes Tab */
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {notesItems.length > 0 ? (
                  notesItems.map((item, idx) => (
                    <div key={idx} className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${item.color}`}>
                        <StickyNote size={14} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-700">{item.description}</p>
                        <div className="flex items-center gap-2 mt-1.5">
                          {item.metadata?.project_name && (
                            <span className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">
                              {item.metadata.project_name}
                            </span>
                          )}
                          {item.metadata?.source === 'visit_completion' && (
                            <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                              Visit Note
                            </span>
                          )}
                          <span className="text-xs text-slate-400">{formatDateTime(item.datetime)}</span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : customerNotes.length > 0 ? (
                  /* Fallback to customer notes if no timeline notes */
                  customerNotes.slice(0, 10).map((note, idx) => (
                    <div key={idx} className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-cyan-50 text-cyan-600">
                        <StickyNote size={14} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-700">{note.content}</p>
                        <div className="flex items-center gap-2 mt-1.5">
                          {note.source === 'visit_completion' && (
                            <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                              Visit Note
                            </span>
                          )}
                          <span className="text-xs text-slate-500">{note.created_by_name}</span>
                          <span className="text-xs text-slate-400">• {formatDateTime(note.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <StickyNote size={24} className="mx-auto text-slate-300 mb-2" />
                    <p className="text-sm text-slate-500">No notes yet</p>
                    <p className="text-xs text-slate-400 mt-1">Add notes manually or from visit completion</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {projectGroupedVisits.length > 0 ? (
                  projectGroupedVisits.map((project, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Building2 size={16} className="text-purple-600" />
                          <p className="font-medium text-slate-900">{project.project_name}</p>
                        </div>
                        <div className="flex gap-1">
                          <Badge className="bg-purple-100 text-purple-700 text-xs">
                            {project.visits?.length || 0} visit{(project.visits?.length || 0) !== 1 ? 's' : ''}
                          </Badge>
                          {project.revisit_count > 0 && (
                            <Badge className="bg-cyan-100 text-cyan-700 text-xs">
                              {project.revisit_count} revisit{project.revisit_count !== 1 ? 's' : ''}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="space-y-1">
                        {project.visits?.slice(0, 3).map((visit, vIdx) => (
                          <div key={vIdx} className="flex items-center gap-2 text-xs">
                            <span className={`w-1.5 h-1.5 rounded-full ${
                              visit.status === 'COMPLETED' ? 'bg-emerald-500' : 
                              visit.status === 'CANCELLED' ? 'bg-red-400' : 'bg-purple-500'
                            }`}></span>
                            <span className="text-slate-600">{formatDate(visit.visit_datetime)}</span>
                            <span className={`${
                              visit.status === 'COMPLETED' ? 'text-emerald-600' : 
                              visit.status === 'CANCELLED' ? 'text-red-500' : 'text-purple-600'
                            }`}>
                              {visit.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <FolderOpen size={24} className="mx-auto text-slate-300 mb-2" />
                    <p className="text-sm text-slate-500">No projects explored yet</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* ============== DIALOGS ============== */}

      {/* Schedule Meeting Dialog */}
      <Dialog open={showScheduleVisitDialog} onOpenChange={setShowScheduleVisitDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Schedule Meeting</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Meeting Type Selection */}
            <div>
              <Label className="text-sm font-medium">Select Meeting Type</Label>
              <div className="grid grid-cols-1 gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setScheduleVisitForm({...scheduleVisitForm, meeting_type: 'Site Visit', obm_purpose: ''})}
                  className={`p-3 rounded-lg text-left border transition-all ${
                    scheduleVisitForm.meeting_type === 'Site Visit' 
                      ? 'border-purple-400 bg-purple-50 ring-2 ring-purple-200' 
                      : 'border-slate-200 bg-white hover:border-purple-200 hover:bg-purple-50/50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🏗️</span>
                    <span className="font-medium text-slate-900">Project Site Visit</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 ml-7">Meeting at the project location with the customer</p>
                </button>
                <button
                  type="button"
                  onClick={() => setScheduleVisitForm({...scheduleVisitForm, meeting_type: 'OBM', obm_purpose: 'Project Discussion'})}
                  className={`p-3 rounded-lg text-left border transition-all ${
                    scheduleVisitForm.meeting_type === 'OBM' 
                      ? 'border-amber-400 bg-amber-50 ring-2 ring-amber-200' 
                      : 'border-slate-200 bg-white hover:border-amber-200 hover:bg-amber-50/50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">☕</span>
                    <span className="font-medium text-slate-900">OBM (Outbound Meeting)</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 ml-7">Meeting outside project site (café, office, home visit, gifting, etc.)</p>
                </button>
              </div>
            </div>
            
            {/* OBM Purpose - Only shown when OBM is selected */}
            {scheduleVisitForm.meeting_type === 'OBM' && (
              <div>
                <Label className="text-sm">Meeting Purpose *</Label>
                <Select value={scheduleVisitForm.obm_purpose} onValueChange={(val) => setScheduleVisitForm({...scheduleVisitForm, obm_purpose: val})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select purpose..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Project Discussion">Project Discussion</SelectItem>
                    <SelectItem value="Requirement Discussion">Requirement Discussion</SelectItem>
                    <SelectItem value="Negotiation">Negotiation</SelectItem>
                    <SelectItem value="Relationship Meeting">Relationship Meeting</SelectItem>
                    <SelectItem value="Document Collection">Document Collection</SelectItem>
                    <SelectItem value="Gift Delivery">🎁 Gift Delivery</SelectItem>
                    <SelectItem value="Customer Home Visit">🏠 Customer Home Visit</SelectItem>
                    <SelectItem value="Office Meeting">🏢 Office Meeting</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label className="text-sm">{scheduleVisitForm.meeting_type === 'Site Visit' ? 'Project *' : 'Related Project (Optional)'}</Label>
              <ProjectSearchPicker
                value={scheduleVisitForm.project_id ? { project_id: scheduleVisitForm.project_id, project_name: scheduleVisitForm.project_name } : null}
                onChange={(project) => {
                  if (project) {
                    setScheduleVisitForm({...scheduleVisitForm, project_id: project.project_id, project_name: project.project_name});
                  } else {
                    setScheduleVisitForm({...scheduleVisitForm, project_id: '', project_name: ''});
                  }
                }}
                userCity={user?.city}
                className="mt-1"
                placeholder={scheduleVisitForm.meeting_type === 'Site Visit' ? 'Search project...' : 'Search project (optional)...'}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Date *</Label>
                <Input type="date" value={scheduleVisitForm.visit_date} onChange={(e) => setScheduleVisitForm({...scheduleVisitForm, visit_date: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label className="text-sm">Time *</Label>
                <Input type="time" value={scheduleVisitForm.visit_time} onChange={(e) => setScheduleVisitForm({...scheduleVisitForm, visit_time: e.target.value})} className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-sm">Notes / Remarks</Label>
              <Textarea value={scheduleVisitForm.notes} onChange={(e) => setScheduleVisitForm({...scheduleVisitForm, notes: e.target.value})} className="mt-1" placeholder="Optional notes..." rows={2} />
            </div>
            <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg">
              <input type="checkbox" id="reminder" checked={scheduleVisitForm.auto_reminder} onChange={(e) => setScheduleVisitForm({...scheduleVisitForm, auto_reminder: e.target.checked})} className="rounded" />
              <Label htmlFor="reminder" className="text-sm text-purple-700 cursor-pointer">Enable auto-reminder</Label>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowScheduleVisitDialog(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleScheduleVisit} className={`flex-1 ${scheduleVisitForm.meeting_type === 'OBM' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-purple-600 hover:bg-purple-700'}`}>
                {scheduleVisitForm.meeting_type === 'Site Visit' ? '🏗️ Schedule Site Visit' : '☕ Schedule OBM'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mark Complete Dialog */}
      <Dialog open={showCompleteVisitDialog} onOpenChange={(open) => { if (!open) { setShowCompleteVisitDialog(false); setSelectedVisit(null); setCompleteVisitForm({ project_id: '', project_name: '', completed_date: '', completed_time: '', remarks: '', next_followup_date: '', next_followup_time: '', lead_warmth: '' }); }}}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark Visit as Completed</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedVisit && (
              <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                <p className="font-medium text-emerald-900">{selectedVisit.project_name}</p>
                <p className="text-xs text-emerald-600">Scheduled: {formatDateTime(selectedVisit.visit_datetime)}</p>
              </div>
            )}
            <div>
              <Label className="text-sm">Confirm Project</Label>
              <ProjectSearchPicker
                value={completeVisitForm.project_id ? { project_id: completeVisitForm.project_id, project_name: completeVisitForm.project_name } : (selectedVisit ? { project_id: selectedVisit.project_id, project_name: selectedVisit.project_name } : null)}
                onChange={(project) => {
                  if (project) {
                    setCompleteVisitForm({...completeVisitForm, project_id: project.project_id, project_name: project.project_name});
                  } else {
                    setCompleteVisitForm({...completeVisitForm, project_id: '', project_name: ''});
                  }
                }}
                userCity={user?.city}
                className="mt-1"
                placeholder="Search project..."
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm flex items-center gap-1">
                  Completed Date
                  <span className="text-xs text-slate-400">(editable)</span>
                </Label>
                <Input type="date" value={completeVisitForm.completed_date} onChange={(e) => setCompleteVisitForm({...completeVisitForm, completed_date: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label className="text-sm flex items-center gap-1">
                  Time
                  <span className="text-xs text-slate-400">(editable)</span>
                </Label>
                <Input type="time" value={completeVisitForm.completed_time} onChange={(e) => setCompleteVisitForm({...completeVisitForm, completed_time: e.target.value})} className="mt-1" />
              </div>
            </div>
            
            {/* Lead Warmth Selection */}
            <div>
              <Label className="text-sm">Lead Warmth</Label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setCompleteVisitForm({...completeVisitForm, lead_warmth: ''})}
                  className={`p-2 rounded-lg text-xs font-medium border transition-all ${
                    !completeVisitForm.lead_warmth 
                      ? 'border-slate-400 bg-slate-100 text-slate-700' 
                      : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                  }`}
                >
                  No Change
                </button>
                <button
                  type="button"
                  onClick={() => setCompleteVisitForm({...completeVisitForm, lead_warmth: 'Hot'})}
                  className={`p-2 rounded-lg text-xs font-medium border transition-all ${
                    completeVisitForm.lead_warmth === 'Hot' 
                      ? 'border-red-400 bg-red-50 text-red-700' 
                      : 'border-slate-200 bg-white text-slate-500 hover:border-red-200 hover:bg-red-50'
                  }`}
                >
                  🔥 Hot
                </button>
                <button
                  type="button"
                  onClick={() => setCompleteVisitForm({...completeVisitForm, lead_warmth: 'Warm'})}
                  className={`p-2 rounded-lg text-xs font-medium border transition-all ${
                    completeVisitForm.lead_warmth === 'Warm' 
                      ? 'border-amber-400 bg-amber-50 text-amber-700' 
                      : 'border-slate-200 bg-white text-slate-500 hover:border-amber-200 hover:bg-amber-50'
                  }`}
                >
                  ☀️ Warm
                </button>
                <button
                  type="button"
                  onClick={() => setCompleteVisitForm({...completeVisitForm, lead_warmth: 'Cold'})}
                  className={`p-2 rounded-lg text-xs font-medium border transition-all ${
                    completeVisitForm.lead_warmth === 'Cold' 
                      ? 'border-blue-400 bg-blue-50 text-blue-700' 
                      : 'border-slate-200 bg-white text-slate-500 hover:border-blue-200 hover:bg-blue-50'
                  }`}
                >
                  ❄️ Cold
                </button>
              </div>
            </div>
            
            <div>
              <Label className="text-sm">Visit Notes</Label>
              <p className="text-xs text-slate-400 mb-1">These will be saved to customer notes</p>
              <Textarea value={completeVisitForm.remarks} onChange={(e) => setCompleteVisitForm({...completeVisitForm, remarks: e.target.value})} className="mt-1" placeholder="How did the visit go? Client feedback, interests, concerns..." rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Next Follow-up</Label>
                <Input type="date" value={completeVisitForm.next_followup_date} onChange={(e) => setCompleteVisitForm({...completeVisitForm, next_followup_date: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label className="text-sm">Time</Label>
                <Input type="time" value={completeVisitForm.next_followup_time} onChange={(e) => setCompleteVisitForm({...completeVisitForm, next_followup_time: e.target.value})} className="mt-1" />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => { setShowCompleteVisitDialog(false); setSelectedVisit(null); setCompleteVisitForm({ project_id: '', project_name: '', completed_date: '', completed_time: '', remarks: '', next_followup_date: '', next_followup_time: '', lead_warmth: '' }); }} className="flex-1">Cancel</Button>
              <Button onClick={handleMarkVisitCompleted} className="flex-1 bg-emerald-600 hover:bg-emerald-700">Mark Completed</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Visit Dialog */}
      <Dialog open={showCancelVisitDialog} onOpenChange={(open) => { if (!open) { setShowCancelVisitDialog(false); setSelectedVisit(null); }}}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel Visit</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedVisit && (
              <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                <p className="font-medium text-red-900">{selectedVisit.project_name}</p>
                <p className="text-xs text-red-600">Scheduled: {formatDateTime(selectedVisit.visit_datetime)}</p>
              </div>
            )}
            <div>
              <Label className="text-sm">Cancel Reason *</Label>
              <Select value={cancelVisitForm.cancel_reason} onValueChange={(v) => setCancelVisitForm({...cancelVisitForm, cancel_reason: v})}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent>
                  {VISIT_CANCEL_REASONS.map(reason => (
                    <SelectItem key={reason} value={reason}>{reason}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Notes</Label>
              <Textarea value={cancelVisitForm.notes} onChange={(e) => setCancelVisitForm({...cancelVisitForm, notes: e.target.value})} className="mt-1" placeholder="Additional details..." rows={2} />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => { setShowCancelVisitDialog(false); setSelectedVisit(null); }} className="flex-1">Go Back</Button>
              <Button onClick={handleCancelVisit} disabled={!cancelVisitForm.cancel_reason} className="flex-1 bg-red-600 hover:bg-red-700">Cancel Visit</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reschedule Visit Dialog */}
      <Dialog open={showRescheduleVisitDialog} onOpenChange={(open) => { if (!open) { setShowRescheduleVisitDialog(false); setSelectedVisit(null); }}}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reschedule Visit</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedVisit && (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                <p className="font-medium text-amber-900">{selectedVisit.project_name}</p>
                <p className="text-xs text-amber-600">Originally: {formatDateTime(selectedVisit.visit_datetime)}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">New Date *</Label>
                <Input type="date" value={rescheduleForm.new_date} onChange={(e) => setRescheduleForm({...rescheduleForm, new_date: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label className="text-sm">New Time *</Label>
                <Input type="time" value={rescheduleForm.new_time} onChange={(e) => setRescheduleForm({...rescheduleForm, new_time: e.target.value})} className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-sm">Notes</Label>
              <Textarea value={rescheduleForm.notes} onChange={(e) => setRescheduleForm({...rescheduleForm, notes: e.target.value})} className="mt-1" placeholder="Reason for rescheduling..." rows={2} />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => { setShowRescheduleVisitDialog(false); setSelectedVisit(null); }} className="flex-1">Cancel</Button>
              <Button onClick={handleRescheduleVisit} disabled={!rescheduleForm.new_date || !rescheduleForm.new_time} className="flex-1 bg-amber-600 hover:bg-amber-700">Reschedule</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Follow-up Dialog */}
      <Dialog open={showFollowupDialog} onOpenChange={setShowFollowupDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Follow-up</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-sm">Follow-up Type</Label>
              <Select value={followupForm.type} onValueChange={(v) => setFollowupForm({...followupForm, type: v})}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Call follow-up">Call</SelectItem>
                  <SelectItem value="WhatsApp follow-up">WhatsApp</SelectItem>
                  <SelectItem value="Email follow-up">Email</SelectItem>
                  <SelectItem value="Meeting">Meeting</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Due Date *</Label>
                <Input type="date" value={followupForm.due_date} onChange={(e) => setFollowupForm({...followupForm, due_date: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label className="text-sm">Time</Label>
                <Input type="time" value={followupForm.due_time} onChange={(e) => setFollowupForm({...followupForm, due_time: e.target.value})} className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-sm">Notes</Label>
              <Textarea value={followupForm.notes} onChange={(e) => setFollowupForm({...followupForm, notes: e.target.value})} className="mt-1" placeholder="What to discuss..." rows={2} />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowFollowupDialog(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleAddFollowup} className="flex-1 bg-purple-600 hover:bg-purple-700">Add Follow-up</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Booking Dialog */}
      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Booking</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Only show projects where customer has completed a site visit */}
            {(() => {
              // Filter projects with at least one COMPLETED visit
              const completedVisitProjects = (customer?.project_wise_visits || []).filter(
                project => project.visits?.some(v => v.status === 'COMPLETED')
              );
              
              if (completedVisitProjects.length === 0) {
                return (
                  <div className="p-4 bg-amber-50 rounded-xl border border-amber-200 text-center">
                    <AlertTriangle size={24} className="mx-auto text-amber-500 mb-2" />
                    <p className="text-sm font-medium text-amber-800">No Completed Site Visits</p>
                    <p className="text-xs text-amber-600 mt-1">
                      A booking can only be created for projects where the customer has completed a site visit.
                    </p>
                  </div>
                );
              }
              
              return (
                <div>
                  <Label className="text-sm">Project (Visited) *</Label>
                  <Select 
                    value={bookingForm.project_id} 
                    onValueChange={(projectId) => {
                      const project = completedVisitProjects.find(p => p.project_id === projectId);
                      setBookingForm({
                        ...bookingForm, 
                        project_id: projectId, 
                        project_name: project?.project_name || ''
                      });
                    }}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select visited project" />
                    </SelectTrigger>
                    <SelectContent>
                      {completedVisitProjects.map((project) => {
                        const visitCount = project.visits?.filter(v => v.status === 'COMPLETED').length || 0;
                        return (
                          <SelectItem key={project.project_id} value={project.project_id}>
                            {project.project_name} ({visitCount} visit{visitCount !== 1 ? 's' : ''})
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-slate-500 mt-1">
                    Only projects with completed site visits are shown
                  </p>
                </div>
              );
            })()}
            <div>
              <Label className="text-sm">Agreement Value (₹ Lacs) *</Label>
              <Input type="number" value={bookingForm.agreement_value} onChange={(e) => setBookingForm({...bookingForm, agreement_value: e.target.value})} className="mt-1" placeholder="e.g., 50 for ₹50 Lacs" />
            </div>
            <div>
              <Label className="text-sm">Booking Date</Label>
              <Input type="date" value={bookingForm.booking_date} onChange={(e) => setBookingForm({...bookingForm, booking_date: e.target.value})} className="mt-1" />
            </div>
            <div>
              <Label className="text-sm">Remarks</Label>
              <Textarea value={bookingForm.remarks} onChange={(e) => setBookingForm({...bookingForm, remarks: e.target.value})} className="mt-1" placeholder="Any notes..." rows={2} />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowBookingDialog(false)} className="flex-1">Cancel</Button>
              <Button 
                onClick={handleAddBooking} 
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                disabled={!bookingForm.project_id || !(customer?.project_wise_visits || []).some(p => p.visits?.some(v => v.status === 'COMPLETED'))}
              >
                Create Booking
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mark Lost Dialog */}
      <Dialog open={showLostDialog} onOpenChange={setShowLostDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Submit for Lost Review</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-700">
                <span className="font-medium">Review Process:</span> This lead will be submitted to your City Head for review. They will decide to:
              </p>
              <ul className="text-xs text-amber-600 mt-2 space-y-1 list-disc list-inside">
                <li>Confirm as Lost (permanently close)</li>
                <li>Reopen the lead for another attempt</li>
                <li>Reassign to a different rep</li>
              </ul>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-sm text-slate-600">
                Lead: <span className="font-medium">{customer.customer_name}</span>
              </p>
            </div>
            <div>
              <Label className="text-sm">Lost Reason *</Label>
              <Select value={lostForm.reason} onValueChange={(v) => setLostForm({...lostForm, reason: v})}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent>
                  {LOST_REASONS.map(reason => (
                    <SelectItem key={reason} value={reason}>{reason}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Final Remark *</Label>
              <Textarea value={lostForm.notes} onChange={(e) => setLostForm({...lostForm, notes: e.target.value})} className="mt-1" placeholder="Why is this lead being marked lost? Be specific to help City Head review." rows={3} />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowLostDialog(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleMarkLost} disabled={!lostForm.reason || !lostForm.notes?.trim()} className="flex-1 bg-amber-600 hover:bg-amber-700">Submit for Review</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Requirement Dialog */}
      <Dialog open={showRequirementDialog} onOpenChange={setShowRequirementDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Requirements</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-2 bg-amber-50 rounded-lg border border-amber-100">
              <p className="text-xs text-amber-700">
                {requirementEditsRemaining} edit{requirementEditsRemaining !== 1 ? 's' : ''} remaining
              </p>
            </div>
            <div>
              <Label className="text-sm">Budget (in Lacs)</Label>
              <div className="flex gap-2 mt-1">
                <div className="flex-1">
                  <Input 
                    type="number"
                    value={requirementForm.budgetMin} 
                    onChange={(e) => setRequirementForm({...requirementForm, budgetMin: e.target.value})} 
                    placeholder="Min (e.g., 50)"
                    data-testid="budget-min-input"
                  />
                  <span className="text-xs text-slate-500">Min</span>
                </div>
                <div className="flex items-center text-slate-400">-</div>
                <div className="flex-1">
                  <Input 
                    type="number"
                    value={requirementForm.budgetMax} 
                    onChange={(e) => setRequirementForm({...requirementForm, budgetMax: e.target.value})} 
                    placeholder="Max (e.g., 100)"
                    data-testid="budget-max-input"
                  />
                  <span className="text-xs text-slate-500">Max</span>
                </div>
              </div>
            </div>
            <div>
              <Label className="text-sm">Location</Label>
              <Input value={requirementForm.location} onChange={(e) => setRequirementForm({...requirementForm, location: e.target.value})} className="mt-1" placeholder="e.g., Whitefield, Bangalore" />
            </div>
            <div>
              <Label className="text-sm">Purpose</Label>
              <Select value={requirementForm.purpose} onValueChange={(v) => setRequirementForm({...requirementForm, purpose: v})}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select purpose" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="End-use">End-use</SelectItem>
                  <SelectItem value="Investment">Investment</SelectItem>
                  <SelectItem value="Both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowRequirementDialog(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleUpdateRequirement} className="flex-1 bg-purple-600 hover:bg-purple-700">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Referral Dialog - Keyboard-safe */}
      <Dialog open={showReferralDialog} onOpenChange={setShowReferralDialog}>
        <DialogContent className="max-w-md max-h-[85dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Referral</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-sm">Referral Name *</Label>
              <Input value={referralForm.name} onChange={(e) => setReferralForm({...referralForm, name: e.target.value})} className="mt-1" placeholder="Full name" />
            </div>
            <div>
              <Label className="text-sm">Phone *</Label>
              <Input value={referralForm.phone} onChange={(e) => setReferralForm({...referralForm, phone: e.target.value})} className="mt-1" placeholder="10-digit mobile" />
            </div>
            <div>
              <Label className="text-sm">Email</Label>
              <Input type="email" value={referralForm.email} onChange={(e) => setReferralForm({...referralForm, email: e.target.value})} className="mt-1" placeholder="Optional" />
            </div>
            <div className="flex gap-2 pt-2 sticky bottom-0 bg-white pb-1">
              <Button variant="outline" onClick={() => setShowReferralDialog(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleAddReferral} disabled={!referralForm.name || !referralForm.phone} className="flex-1 bg-purple-600 hover:bg-purple-700">Add Referral</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Notes Dialog (Bottom Sheet style on mobile) */}
      <Dialog open={showNotesDialog} onOpenChange={setShowNotesDialog}>
        <DialogContent className="max-w-md sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote size={18} className="text-amber-600" />
              Add Note
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Textarea
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                placeholder="Add a note..."
                className="min-h-[120px] resize-none"
                data-testid="note-content-input"
              />
              <p className="text-xs text-slate-400 mt-1">
                {noteContent.length} characters
              </p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button 
                variant="outline" 
                onClick={() => { setShowNotesDialog(false); setNoteContent(''); }} 
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAddNote} 
                disabled={!noteContent.trim() || notesLoading}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                data-testid="save-note-btn"
              >
                {notesLoading ? 'Saving...' : 'Save Note'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View All Notes Dialog */}
      <Dialog open={showAllNotesDialog} onOpenChange={setShowAllNotesDialog}>
        <DialogContent className="max-w-md sm:max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote size={18} className="text-amber-600" />
              All Notes ({customerNotes.length})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4 max-h-[60vh] overflow-y-auto">
            {customerNotes.map((note, idx) => (
              <div key={note.note_id || idx} className="p-3 bg-amber-50/50 rounded-xl border border-amber-100">
                <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">{note.content}</p>
                <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                  <span>{note.created_by_name}</span>
                  <span>•</span>
                  <span>{formatDateTime(note.created_at)}</span>
                </div>
              </div>
            ))}
            {customerNotes.length === 0 && (
              <div className="text-center py-8">
                <StickyNote size={24} className="mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">No notes yet</p>
              </div>
            )}
          </div>
          <div className="flex gap-2 pt-2">
            <Button 
              variant="outline" 
              onClick={() => setShowAllNotesDialog(false)} 
              className="flex-1"
            >
              Close
            </Button>
            <Button 
              onClick={() => { setShowAllNotesDialog(false); setShowNotesDialog(true); }}
              className="flex-1 bg-purple-600 hover:bg-purple-700"
            >
              <Plus size={16} className="mr-2" />
              Add Note
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Smart Note Dialog */}
      <Dialog open={showWhatsAppDialog} onOpenChange={setShowWhatsAppDialog}>
        <DialogContent className="max-w-md sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle size={18} className="text-green-600" />
              Share Note via WhatsApp
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-3 bg-slate-50 rounded-xl">
              <p className="text-xs text-slate-500 mb-1">Original Note:</p>
              <p className="text-sm text-slate-700">{lastSavedNote}</p>
            </div>
            
            {/* Tone Selector */}
            <div>
              <Label className="text-sm font-medium">Message Tone</Label>
              <div className="flex gap-2 mt-2">
                {['Formal', 'Concise', 'Premium Luxury'].map((tone) => (
                  <Button
                    key={tone}
                    variant={whatsAppTone === tone ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      setWhatsAppTone(tone);
                      setWhatsAppMessage(''); // Reset message when tone changes
                    }}
                    className={`flex-1 text-xs ${whatsAppTone === tone ? 'bg-green-600 hover:bg-green-700' : ''}`}
                  >
                    {tone}
                  </Button>
                ))}
              </div>
            </div>

            {/* Convert Button */}
            {!whatsAppMessage && (
              <Button
                onClick={handleConvertToWhatsApp}
                disabled={whatsAppConverting}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                {whatsAppConverting ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Converting with AI...
                  </>
                ) : (
                  <>
                    <MessageCircle size={16} className="mr-2" />
                    Convert to WhatsApp Message
                  </>
                )}
              </Button>
            )}

            {/* Generated Message Preview */}
            {whatsAppMessage && (
              <div>
                <Label className="text-sm font-medium">WhatsApp Message Preview</Label>
                <Textarea
                  value={whatsAppMessage}
                  onChange={(e) => setWhatsAppMessage(e.target.value)}
                  className="mt-2 min-h-[200px] text-sm"
                  placeholder="Generated message will appear here..."
                />
                <p className="text-xs text-slate-400 mt-1">
                  You can edit the message before sharing
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2 pt-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowWhatsAppDialog(false);
                  setWhatsAppMessage('');
                  setLastSavedNote('');
                }} 
                className="flex-1"
              >
                Close
              </Button>
              
              {whatsAppMessage && (
                <>
                  <Button
                    variant="outline"
                    onClick={handleCopyToClipboard}
                    className="flex-1"
                  >
                    <Copy size={16} className="mr-2" />
                    Copy
                  </Button>
                  <Button
                    onClick={handleShareViaWhatsApp}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    <ExternalLink size={16} className="mr-2" />
                    Share
                  </Button>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog open={showEditCustomerDialog} onOpenChange={setShowEditCustomerDialog}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit size={18} className="text-purple-600" />
              Edit Customer
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-3">
            <div>
              <Label className="text-xs font-medium text-slate-600">Name</Label>
              <Input 
                value={editCustomerForm.customer_name} 
                onChange={(e) => setEditCustomerForm({...editCustomerForm, customer_name: e.target.value})} 
                className="mt-1 rounded-xl" 
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Email</Label>
              <Input 
                type="email"
                value={editCustomerForm.customer_email} 
                onChange={(e) => setEditCustomerForm({...editCustomerForm, customer_email: e.target.value})} 
                className="mt-1 rounded-xl" 
                placeholder="customer@email.com"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Phone</Label>
              <Input 
                value={editCustomerForm.customer_phone} 
                onChange={(e) => setEditCustomerForm({...editCustomerForm, customer_phone: e.target.value})} 
                className="mt-1 rounded-xl" 
                placeholder="+91 9999999999"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-medium text-slate-600">Budget Min (₹L)</Label>
                <Input 
                  type="number" 
                  value={editCustomerForm.budget_min_lacs} 
                  onChange={(e) => setEditCustomerForm({...editCustomerForm, budget_min_lacs: e.target.value})} 
                  className="mt-1 rounded-xl" 
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Budget Max (₹L)</Label>
                <Input 
                  type="number" 
                  value={editCustomerForm.budget_max_lacs} 
                  onChange={(e) => setEditCustomerForm({...editCustomerForm, budget_max_lacs: e.target.value})} 
                  className="mt-1 rounded-xl" 
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Locations (comma separated)</Label>
              <Input 
                value={Array.isArray(editCustomerForm.preferred_locations) ? editCustomerForm.preferred_locations.join(', ') : ''} 
                onChange={(e) => setEditCustomerForm({...editCustomerForm, preferred_locations: e.target.value.split(',').map(l => l.trim()).filter(l => l)})} 
                className="mt-1 rounded-xl" 
                placeholder="Whitefield, Sarjapur, Electronic City"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-medium text-slate-600">Configuration</Label>
                <Select value={editCustomerForm.configuration || ''} onValueChange={(v) => setEditCustomerForm({...editCustomerForm, configuration: v})}>
                  <SelectTrigger className="mt-1 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1BHK">1 BHK</SelectItem>
                    <SelectItem value="2BHK">2 BHK</SelectItem>
                    <SelectItem value="3BHK">3 BHK</SelectItem>
                    <SelectItem value="4BHK+">4 BHK+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Purpose</Label>
                <Select value={editCustomerForm.purpose || 'End-use'} onValueChange={(v) => setEditCustomerForm({...editCustomerForm, purpose: v})}>
                  <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="End-use">End-use</SelectItem>
                    <SelectItem value="Investment">Investment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setShowEditCustomerDialog(false)}>
                Cancel
              </Button>
              <Button className="flex-1 bg-[#5B3DF5] hover:bg-[#4A2FE0] text-white rounded-xl" onClick={handleUpdateCustomer}>
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Follow-up Action Dialog (Reschedule/Complete) */}
      <Dialog open={showFollowupActionDialog} onOpenChange={(open) => {
        if (!open) {
          setShowFollowupActionDialog(false);
          setSelectedFollowup(null);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock size={20} className="text-amber-600" />
              Manage Follow-up
            </DialogTitle>
          </DialogHeader>
          
          {selectedFollowup && (
            <div className="space-y-4 py-2">
              {/* Current Follow-up Info */}
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                <div className="flex items-center gap-2 mb-1">
                  <Badge className="text-xs bg-amber-100 text-amber-700">
                    {selectedFollowup.followup_type || 'Follow-up'}
                  </Badge>
                  {selectedFollowup.linked_task_id && (
                    <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200">
                      📌 Synced with My Tasks
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-slate-700 mt-1">{selectedFollowup.notes || 'No notes'}</p>
                <p className="text-xs text-slate-500 mt-1">
                  Current Due: {selectedFollowup.due_datetime ? formatDateTime(selectedFollowup.due_datetime) : 'Not set'}
                </p>
              </div>
              
              {/* Action Selection */}
              <div>
                <Label className="text-sm font-medium">What would you like to do?</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => setFollowupActionForm({...followupActionForm, action: 'reschedule'})}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      followupActionForm.action === 'reschedule' 
                        ? 'border-amber-500 bg-amber-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <RotateCcw size={18} className="text-amber-600 mb-1" />
                    <p className="font-medium text-sm">Reschedule</p>
                    <p className="text-xs text-slate-500">Change date/time</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFollowupActionForm({...followupActionForm, action: 'complete'})}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      followupActionForm.action === 'complete' 
                        ? 'border-emerald-500 bg-emerald-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <CheckCircle size={18} className="text-emerald-600 mb-1" />
                    <p className="font-medium text-sm">Complete</p>
                    <p className="text-xs text-slate-500">Mark as done</p>
                  </button>
                </div>
              </div>
              
              {/* Reschedule Form */}
              {followupActionForm.action === 'reschedule' && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">New Date *</Label>
                      <Input 
                        type="date" 
                        value={followupActionForm.new_date}
                        onChange={(e) => setFollowupActionForm({...followupActionForm, new_date: e.target.value})}
                        className="mt-1 rounded-xl"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">New Time</Label>
                      <Input 
                        type="time" 
                        value={followupActionForm.new_time}
                        onChange={(e) => setFollowupActionForm({...followupActionForm, new_time: e.target.value})}
                        className="mt-1 rounded-xl"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Notes (optional)</Label>
                    <Textarea 
                      value={followupActionForm.notes}
                      onChange={(e) => setFollowupActionForm({...followupActionForm, notes: e.target.value})}
                      rows={2}
                      className="mt-1 rounded-xl"
                      placeholder="Update notes..."
                    />
                  </div>
                </div>
              )}
              
              {/* Complete Form */}
              {followupActionForm.action === 'complete' && (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Outcome</Label>
                    <Select 
                      value={followupActionForm.outcome}
                      onValueChange={(v) => setFollowupActionForm({...followupActionForm, outcome: v})}
                    >
                      <SelectTrigger className="mt-1 rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Connected">Connected</SelectItem>
                        <SelectItem value="Not Reachable">Not Reachable</SelectItem>
                        <SelectItem value="Call Back Later">Call Back Later</SelectItem>
                        <SelectItem value="Interested">Interested</SelectItem>
                        <SelectItem value="Not Interested">Not Interested</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Remark (optional)</Label>
                    <Textarea 
                      value={followupActionForm.outcome_remark}
                      onChange={(e) => setFollowupActionForm({...followupActionForm, outcome_remark: e.target.value})}
                      rows={2}
                      className="mt-1 rounded-xl"
                      placeholder="Add completion notes..."
                    />
                  </div>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1 rounded-xl"
                  onClick={() => {
                    setShowFollowupActionDialog(false);
                    setSelectedFollowup(null);
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  className={`flex-1 rounded-xl ${
                    followupActionForm.action === 'complete' 
                      ? 'bg-emerald-600 hover:bg-emerald-700' 
                      : 'bg-amber-600 hover:bg-amber-700'
                  } text-white`}
                  onClick={handleSubmitFollowupAction}
                >
                  {followupActionForm.action === 'complete' ? 'Complete Follow-up' : 'Reschedule'}
                </Button>
              </div>
              
              {/* Sync Notice */}
              {selectedFollowup.linked_task_id && (
                <p className="text-xs text-center text-blue-600 bg-blue-50 py-2 rounded-lg">
                  📌 This action will automatically sync to your My Tasks
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerDetailRep;
